export type ErrorDetails = {
	message: string;
	details: string;
};

function normalizeErrorValue(value: unknown, seen = new WeakSet<object>()): unknown {
	if (value instanceof Error) {
		const normalized: Record<string, unknown> = {};
		const errorRecord = value as unknown as Record<string, unknown>;

		for (const key of new Set(['name', 'message', 'stack', ...Object.getOwnPropertyNames(value)])) {
			const prop = errorRecord[key];
			if (prop !== undefined) {
				normalized[key] = normalizeErrorValue(prop, seen);
			}
		}

		return normalized;
	}

	if (Array.isArray(value)) {
		return value.map((item) => normalizeErrorValue(item, seen));
	}

	if (value && typeof value === 'object') {
		if (seen.has(value)) {
			return '[Circular]';
		}
		seen.add(value);

		const normalized: Record<string, unknown> = {};
		for (const key of new Set(Object.getOwnPropertyNames(value))) {
			normalized[key] = normalizeErrorValue((value as Record<string, unknown>)[key], seen);
		}
		return normalized;
	}

	if (typeof value === 'function') {
		return `[Function ${value.name || 'anonymous'}]`;
	}

	if (value === undefined) {
		return '[undefined]';
	}

	return value;
}

export function getErrorMessage(error: unknown): string {
	if (!error) return 'Unknown error';
	if (typeof error === 'string') return error;
	if (error instanceof Error) return error.message || error.toString();
	if (
		typeof error === 'object' &&
		error !== null &&
		'message' in error &&
		typeof error.message === 'string'
	) {
		return error.message;
	}

	const normalized = normalizeErrorValue(error);
	return typeof normalized === 'string' ? normalized : JSON.stringify(normalized, null, 2);
}

export function formatErrorDetails(error: unknown): string {
	if (!error) return 'Unknown error';
	if (typeof error === 'string') return error;
	if (error instanceof Error && error.stack) {
		const normalized = normalizeErrorValue(error);
		if (
			normalized &&
			typeof normalized === 'object' &&
			!Array.isArray(normalized) &&
			'stack' in normalized
		) {
			const { stack: _stack, ...rest } = normalized as Record<string, unknown>;
			if (Object.keys(rest).length === 0) {
				return error.stack;
			}
			return `${error.stack}\n\n${JSON.stringify(rest, null, 2)}`;
		}

		return error.stack;
	}

	const normalized = normalizeErrorValue(error);
	return typeof normalized === 'string' ? normalized : JSON.stringify(normalized, null, 2);
}

export function describeError(error: unknown): ErrorDetails {
	return {
		message: getErrorMessage(error),
		details: formatErrorDetails(error),
	};
}
